import { useState, useEffect, useRef } from 'react'
import { api } from '../lib/api'
import { setToken, setCachedUser } from '../lib/auth'
import type { User } from '../lib/types'

interface Props {
  onLogin: (user: User) => void
}

export default function Login({ onLogin }: Props) {
  const [email, setEmail] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [sent, setSent] = useState(false)
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const invite = params.get('invite')
    if (invite) {
      setInviteCode(invite)
      localStorage.setItem('automates_invite', invite)
    }
  }, [])

  useEffect(() => {
    if (sent) inputRefs.current[0]?.focus()
  }, [sent])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (inviteCode) localStorage.setItem('automates_invite', inviteCode)
      await api.login(email)
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code')
    } finally {
      setLoading(false)
    }
  }

  const handleCodeChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return
    const next = [...code]
    next[index] = value
    setCode(next)
    if (value && index < 5) inputRefs.current[index + 1]?.focus()
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length === 6) {
      setCode(pasted.split(''))
      inputRefs.current[5]?.focus()
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    const fullCode = code.join('')
    if (fullCode.length !== 6) return setError('Enter the full 6-digit code')
    setError('')
    setLoading(true)
    try {
      const storedInvite = inviteCode || localStorage.getItem('automates_invite') || undefined
      const { token, user } = await api.verify(fullCode, storedInvite)
      setToken(token)
      setCachedUser(user)
      localStorage.removeItem('automates_invite')
      onLogin(user)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid or expired code')
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-800 to-brand-900 p-4">
      <div className="card max-w-md w-full p-8">
        <div className="text-center mb-8">
          <span className="text-5xl">🤖</span>
          <h1 className="text-2xl font-bold mt-4">Automates 2026</h1>
          <p className="text-gray-500 mt-1">Conference Companion</p>
        </div>

        {sent ? (
          <form onSubmit={handleVerify} className="space-y-6">
            <div className="text-center space-y-2">
              <div className="text-4xl">📧</div>
              <h2 className="text-lg font-semibold">Enter your code</h2>
              <p className="text-gray-600 text-sm">
                We sent a 6-digit code to <strong>{email}</strong>
              </p>
            </div>

            <div className="flex justify-center gap-2" onPaste={handlePaste}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={el => { inputRefs.current[i] = el }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleCodeChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-brand-600 focus:ring-1 focus:ring-brand-600 outline-none"
                />
              ))}
            </div>

            {inviteCode && (
              <p className="text-sm text-green-600 text-center">
                Invite code <strong>{inviteCode}</strong> will be applied automatically.
              </p>
            )}

            {error && <p className="text-red-600 text-sm text-center">{error}</p>}

            <button type="submit" disabled={loading || code.join('').length !== 6} className="btn-primary w-full">
              {loading ? 'Verifying...' : 'Sign In'}
            </button>

            <div className="flex justify-between text-sm">
              <button type="button" onClick={() => { setSent(false); setCode(['', '', '', '', '', '']); setError('') }} className="text-brand-600 hover:underline">
                Use a different email
              </button>
              <button type="button" onClick={handleSubmit} className="text-brand-600 hover:underline">
                Resend code
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="input"
                required
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="invite" className="block text-sm font-medium text-gray-700 mb-1">
                Invite Code <span className="text-gray-400">(optional)</span>
              </label>
              <input
                id="invite"
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="Enter invite code for auto-approval"
                className="input font-mono"
              />
            </div>

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <button type="submit" disabled={loading || !email} className="btn-primary w-full">
              {loading ? 'Sending...' : 'Send Sign-In Code'}
            </button>

            <p className="text-xs text-gray-400 text-center">
              No password needed — we'll email you a 6-digit code. Without an invite code, your account will need admin approval.
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
